import React from "react";
import { Button, Form } from "react-bootstrap";
import { countries } from "../Questions/countries";
import { topicLabel } from "./api";
import "./reviews.css";

export const topics = [
  "patent",
  "contracts",
  "tax",
  "trademark",
  "employment",
  "business",
  "other",
];
export const newAdmission = () => ({
  country: "US",
  jurisdiction: "",
  authority: "",
  licenseNumber: "",
  admittedOn: "",
  status: "active",
});
export const newCoverage = () => ({
  topicKey: "contracts",
  jurisdiction: { level: "state_region", countryCode: "US", region: "" },
});
export const emptyQualifications = () => ({
  displayName: "",
  bio: "",
  experienceYears: 0,
  languages: ["English"],
  admissions: [newAdmission()],
  coverage: [newCoverage()],
});

export function CountrySelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Form.Select
      required
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">Choose a country</option>
      {value && !countries.some(c => c.code === value) && (
        <option value={value}>{value} (saved country)</option>
      )}
      {countries.map(c => (
        <option key={c.code} value={c.code}>
          {c.name}
        </option>
      ))}
    </Form.Select>
  );
}

/**
 * Professional details, admissions, and practice coverage. Shared by the
 * public attorney application and an approved attorney's profile.
 */
export default function AttorneyQualifications({
  form,
  onChange,
  showAvailability = false,
}: {
  form: any;
  onChange: (key: string, value: any) => void;
  showAvailability?: boolean;
}) {
  const change = onChange;
  function item(key: string, index: number, field: string, value: any) {
    change(
      key,
      form[key].map((old: any, i: number) =>
        i === index ? { ...old, [field]: value } : old
      )
    );
  }
  return (
    <>
      <section className="review-section">
        <h2>Professional details</h2>
        <div className="review-form-grid">
          <Form.Group controlId="attorney-name">
            <Form.Label>Professional display name</Form.Label>
            <Form.Control
              required
              maxLength={200}
              value={form.displayName}
              onChange={e => change("displayName", e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="attorney-years">
            <Form.Label>Years of experience (self-reported)</Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={80}
              required
              value={form.experienceYears}
              onChange={e => change("experienceYears", Number(e.target.value))}
            />
          </Form.Group>
        </div>
        <Form.Group className="mt-3" controlId="attorney-bio">
          <Form.Label>Professional introduction</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={2000}
            value={form.bio}
            onChange={e => change("bio", e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mt-3" controlId="attorney-languages">
          <Form.Label>Languages (comma separated)</Form.Label>
          <Form.Control
            required
            value={form.languages.join(", ")}
            onChange={e =>
              change(
                "languages",
                e.target.value.split(",").map(x => x.trim())
              )
            }
          />
        </Form.Group>
        {showAvailability && (
          <Form.Check
            className="mt-3"
            label="Available for new review assignments"
            checked={form.available}
            onChange={e => change("available", e.target.checked)}
          />
        )}
      </section>
      <section className="review-section">
        <h2>Licenses and admissions</h2>
        <p className="text-secondary">
          Admission details are reviewed by an admin. They are separate from the
          work you offer to handle.
        </p>
        {form.admissions.map((a: any, i: number) => (
          <fieldset className="review-form-card" key={i}>
            <legend className="visually-hidden">Admission {i + 1}</legend>
            <div className="review-form-grid">
              {[
                ["country", "Admission country"],
                ["jurisdiction", "Admission jurisdiction"],
                ["authority", "Licensing body"],
                ["licenseNumber", "License / bar number"],
              ].map(([key, label]) => (
                <Form.Group key={key} controlId={`admission-${i}-${key}`}>
                  <Form.Label>{label}</Form.Label>
                  {key === "country" ? (
                    <CountrySelect
                      value={a.country}
                      onChange={value =>
                        item("admissions", i, "country", value)
                      }
                    />
                  ) : (
                    <Form.Control
                      required={key !== "licenseNumber"}
                      maxLength={200}
                      value={a[key]}
                      onChange={e => item("admissions", i, key, e.target.value)}
                    />
                  )}
                </Form.Group>
              ))}
              <Form.Group controlId={`admission-${i}-date`}>
                <Form.Label>Admission date</Form.Label>
                <Form.Control
                  type="date"
                  required
                  value={a.admittedOn}
                  onChange={e =>
                    item("admissions", i, "admittedOn", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group controlId={`admission-${i}-status`}>
                <Form.Label>License status</Form.Label>
                <Form.Select
                  value={a.status}
                  onChange={e =>
                    item("admissions", i, "status", e.target.value)
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </div>
            {form.admissions.length > 1 && (
              <Button
                variant="link"
                onClick={() =>
                  change(
                    "admissions",
                    form.admissions.filter((_: any, n: number) => n !== i)
                  )
                }
              >
                Remove admission
              </Button>
            )}
          </fieldset>
        ))}
        <Button
          variant="outline-dark"
          disabled={form.admissions.length >= 20}
          onClick={() =>
            change("admissions", [...form.admissions, newAdmission()])
          }
        >
          Add admission
        </Button>
      </section>
      <section className="review-section">
        <h2>Practice areas and jurisdictions served</h2>
        <p className="text-secondary">
          Choose the practice areas and locations where you can review
          questions. Each combination needs approval before you receive matching
          assignments.
        </p>
        {form.coverage.map((c: any, i: number) => (
          <fieldset className="review-form-card" key={i}>
            <legend className="visually-hidden">
              Practice area and location {i + 1}
            </legend>
            <div className="review-form-grid">
              <Form.Group controlId={`coverage-${i}-topic`}>
                <Form.Label>Practice area</Form.Label>
                <Form.Select
                  value={c.topicKey}
                  onChange={e =>
                    item("coverage", i, "topicKey", e.target.value)
                  }
                >
                  {c.topicKey && !topics.includes(c.topicKey) && (
                    <option value={c.topicKey}>
                      {topicLabel(c.topicKey)} (saved practice area)
                    </option>
                  )}
                  {topics.map(t => (
                    <option key={t} value={t}>
                      {topicLabel(t)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group controlId={`coverage-${i}-level`}>
                <Form.Label>Jurisdiction type</Form.Label>
                <Form.Select
                  value={c.jurisdiction.level}
                  onChange={e =>
                    item("coverage", i, "jurisdiction", {
                      level: e.target.value,
                      countryCode: c.jurisdiction.countryCode,
                      ...(e.target.value === "state_region"
                        ? { region: "" }
                        : {}),
                    })
                  }
                >
                  <option value="state_region">State / region</option>
                  <option value="federal">Federal</option>
                  <option value="country">Country-wide</option>
                </Form.Select>
              </Form.Group>
              <Form.Group controlId={`coverage-${i}-country`}>
                <Form.Label>Country served</Form.Label>
                <CountrySelect
                  value={c.jurisdiction.countryCode}
                  onChange={value =>
                    item("coverage", i, "jurisdiction", {
                      ...c.jurisdiction,
                      countryCode: value,
                      ...(c.jurisdiction.level === "state_region" &&
                      value !== c.jurisdiction.countryCode
                        ? { region: "" }
                        : {}),
                    })
                  }
                />
              </Form.Group>
              {c.jurisdiction.level === "state_region" && (
                <Form.Group controlId={`coverage-${i}-region`}>
                  <Form.Label>State / region</Form.Label>
                  <Form.Control
                    required
                    maxLength={200}
                    value={c.jurisdiction.region}
                    onChange={e =>
                      item("coverage", i, "jurisdiction", {
                        ...c.jurisdiction,
                        region: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              )}
            </div>
            {form.coverage.length > 1 && (
              <Button
                variant="link"
                onClick={() =>
                  change(
                    "coverage",
                    form.coverage.filter((_: any, n: number) => n !== i)
                  )
                }
              >
                Remove practice area and location
              </Button>
            )}
          </fieldset>
        ))}
        <Button
          variant="outline-dark"
          disabled={form.coverage.length >= 40}
          onClick={() => change("coverage", [...form.coverage, newCoverage()])}
        >
          Add practice area and location
        </Button>
      </section>
    </>
  );
}
